import React, { useEffect, useRef, useState, useCallback } from "react";
import parse, { attributesToProps, domToReact } from "html-react-parser";
import moment from "moment";
import { createRadarModel } from "./yieldRadarModel.js";
import {
  AS_OF,
  PROPERTY,
  CONFIG,
  Q,
  LEVERS,
  RES_COLS,
  PROMO_OWNERS,
  PROMO_TYPE_LABEL,
  buildDatesFromDaily,
  formatEventsList,
  toCalendarYMD,
  formatCalendarDate,
} from "./yieldRadarData.js";
import { commonService } from "../../../../../utils/commonService";
import {
  errorToastMessage,
  successToastMessage,
} from "../../../../../utils/toastMessage";
import { apiCall } from "../../../../../utils/axiosService";
import { API_URL } from "../../../../../utils/apiRoutes";
import { apiResponse } from "../../../../../utils/apiResponse";
import { useSelector } from "react-redux";
import ticket from "../../../../../Assets/Images/Revenue-management/tableTicket.svg";
import EventViewModal from "../../../../CommonComponent/EventViewModal";
import { Modal } from "react-bootstrap";
import Loader from "../../../../Loader/Loader";
import InteractiveLineChart from "./InteractiveLineChart";
import "./YieldRadar.css";

const TABS = {
  actions: "Actions",
  pace: "Pace",
  mix: "Plan mix",
  trace: "Decision trace",
  promo: "Promotions",
  // spec: "Dev spec",
};
const RANK_COLUMNS = [
  { key: "rank", label: "Rank", numeric: true },
  { key: "stayDate", label: "Stay date" },
  { key: "daysOut", label: "Days out", numeric: true },
  { key: "diagnosis", label: "Diagnosis" },
  { key: "score", label: "Score", numeric: true },
  { key: "actions", label: "Actions", numeric: true },
  { key: "revenueAtStake", label: "Revenue at stake", numeric: true },
  { key: "whyFlagged", label: "Why it is flagged" },
];

function Kpi({ label, value, detail }) {
  return (
    <div className="kpi">
      <div className="k">{label}</div>
      <div className="v num">{value}</div>
      <div className="d">{detail}</div>
    </div>
  );
}

function DetailContent({
  html,
  onPlan,
  onReservations,
  onCopyDate,
  onCopyPlan,
  onCopyPromo,
  propertyCode,
  disabled,
}) {
  const content = React.useMemo(() => {
    if (!html) return null;
    const options = {
      replace(node) {
        if (!node.attribs) return;
        const attributes = node.attribs;
        if (attributes["data-chart-interactive"]) {
          try {
            const chartConfig = JSON.parse(
              decodeURIComponent(attributes["data-chart-interactive"])
            );
            return (
              <InteractiveLineChart
                config={chartConfig}
                propertyCode={propertyCode}
                disabled={disabled}
              />
            );
          } catch (err) {
            console.error("Failed to parse interactive chart config:", err);
          }
        }
        if (node.name === "tr" && attributes["data-row"] != null) {
          const open = () => onPlan(attributes["data-row"]);
          return (
            <tr
              {...attributesToProps(attributes)}
              id={commonService.generateGlobalClickTrackerID(
                "YieldRadar",
                `detail-plan-tr-${attributes["data-row"]}`,
                propertyCode,
              )}
              tabIndex={0}
              onClick={open}
              onKeyDown={(event) => {
                if (
                  event.target === event.currentTarget &&
                  (event.key === "Enter" || event.key === " ")
                ) {
                  event.preventDefault();
                  open();
                }
              }}
            >
              {domToReact(node.children, options)}
            </tr>
          );
        }
        if (node.name !== "button") return;
        const click =
          attributes.id === "planRes"
            ? onReservations
            : attributes.id === "copyPlan"
              ? onCopyPlan
              : attributes.id === "copyPromo"
                ? onCopyPromo
                : attributes["data-copy"] === "date"
                  ? onCopyDate
                  : null;
        if (click) {
          const trackerId =
            attributes.id === "planRes"
              ? "plan-reservations-btn"
              : attributes.id === "copyPlan"
                ? "plan-copy-btn"
                : attributes.id === "copyPromo"
                  ? "promo-copy-btn"
                  : attributes["data-copy"] === "date"
                    ? "date-copy-btn"
                    : "btn";
          return (
            <button
              {...attributesToProps(attributes)}
              id={commonService.generateGlobalClickTrackerID(
                "YieldRadar",
                trackerId,
                propertyCode,
              )}
              onClick={click}
            >
              {domToReact(node.children, options)}
            </button>
          );
        }
      },
    };
    return parse(html, options);
  }, [html, propertyCode, disabled, onPlan, onReservations, onCopyDate, onCopyPlan, onCopyPromo]);

  return content;
}

function Actions({
  analysis,
  model,
  excluded,
  approvedKeys,
  onToggle,
  onPlan,
  propertyCode,
  api,
}) {
  const [filterAction, setFilterAction] = useState("all");

  useEffect(() => {
    setFilterAction("all");
  }, [analysis?.s?.d]);

  const maximum =
    Math.max(...analysis.rows.map((row) => row.sell), analysis.h) * 1.05;
  const percent = (value) =>
    Math.max(0, Math.min(100, (value / maximum) * 100));
  const closes = analysis.rows.filter((row) => row.rule === "R9").length;
  const minLos = analysis.rows.filter((row) => row.rule === "R9a").length;
  const reopens = analysis.rows.filter((row) => row.rule === "R6").length;
  const protectedCount = analysis.rows.filter((row) => row.p.protected).length;
  const actionableCount = analysis.rows.filter((row) => row.actionable && !excluded.includes(row.key)).length;

  const filteredRows = analysis.rows.filter((row) => {
    if (filterAction === "bar") return row.rule === "R3" || row.rule === "R4" || row.rule === "R5";
    if (filterAction === "close") return row.rule === "R9";
    if (filterAction === "los") return row.rule === "R9a";
    if (filterAction === "reopen") return row.rule === "R6";
    if (filterAction === "protected") return !!row.p.protected;
    if (filterAction === "selected") return row.actionable && !excluded.includes(row.key);
    return true;
  });

  return (
    <div className="actions-pane">
      <div className="summary">
        {analysis.rows.length > 0 && (
          <button
            type="button"
            className={`pill is-btn ${filterAction === "all" ? "active" : ""}`}
            style={{ border: "none" }}
            onClick={() => setFilterAction("all")}
          >
            All: {analysis.rows.length} plans
          </button>
        )}
        {actionableCount > 0 && (
          <button
            type="button"
            className={`pill is-btn ${filterAction === "selected" ? "active" : ""}`}
            style={{
              border: "none",
              background: filterAction === "selected" ? "var(--under)" : "var(--under-bg)",
              color: filterAction === "selected" ? "#fff" : "var(--under)",
            }}
            onClick={() => setFilterAction(filterAction === "selected" ? "all" : "selected")}
          >
            Selected: {commonService.formateRoom(actionableCount)} plans
          </button>
        )}
        {analysis.mv !== 0 && (
          <button
            type="button"
            className={`pill is-btn ${analysis.mv > 0 ? "raise" : "lower"} ${filterAction === "bar" ? "active" : ""}`}
            style={{ border: "none" }}
            onClick={() => setFilterAction(filterAction === "bar" ? "all" : "bar")}
          >
            BAR {analysis.mv > 0 ? "+" : ""}
            {commonService.formateAmount(analysis.mv)}
          </button>
        )}
        {closes > 0 && (
          <button
            type="button"
            className={`pill is-btn close ${filterAction === "close" ? "active" : ""}`}
            style={{ border: "none" }}
            onClick={() => setFilterAction(filterAction === "close" ? "all" : "close")}
          >
            Close {commonService.formateRoom(closes)} plans
          </button>
        )}
        {minLos > 0 && (
          <button
            type="button"
            className={`pill is-btn los ${filterAction === "los" ? "active" : ""}`}
            style={{ border: "none" }}
            onClick={() => setFilterAction(filterAction === "los" ? "all" : "los")}
          >
            MinLOS on {commonService.formateRoom(minLos)} plans
          </button>
        )}
        {reopens > 0 && (
          <button
            type="button"
            className={`pill is-btn reopen ${filterAction === "reopen" ? "active" : ""}`}
            style={{ border: "none" }}
            onClick={() => setFilterAction(filterAction === "reopen" ? "all" : "reopen")}
          >
            Reopen {commonService.formateRoom(reopens)} plans
          </button>
        )}
        {!analysis.mv && !closes && !minLos && !reopens && (
          <span className="pill hold">No changes needed</span>
        )}
        {protectedCount > 0 && (
          <button
            type="button"
            className={`pill is-btn ${filterAction === "protected" ? "active" : ""}`}
            style={{ border: "none" }}
            onClick={() => setFilterAction(filterAction === "protected" ? "all" : "protected")}
          >
            Protected: {commonService.formateRoom(protectedCount)} contracted plans
          </button>
        )}
      </div>
      <div className="tablewrap actions-tablewrap">
        <table>
          <thead>
            <tr>
              <th>Rate plan</th>
              <th>Channel</th>
              <th className="r">Sell</th>
              <th className="r">Net</th>
              <th>Net vs. hurdle</th>
              <th className="r">Rooms</th>
              <th>Action</th>
              <th>
                <span className="sr">Apply</span>
              </th>
              <th>
                <span className="sr">Details</span>
              </th>
            </tr>
          </thead>
          <tbody id="rows">
            {filteredRows.map((row) => (
              <tr
                key={row.key}
                id={commonService.generateGlobalClickTrackerID(
                  "YieldRadar",
                  `action-plan-row-${row.key}`,
                  propertyCode,
                )}
                className={`clickable ${row.p.closed && row.act !== "Reopen" ? "closedrow" : ""} ${api === 0 ? "disabled-row" : ""}`}
                onClick={() => {
                  if (api === 0) return;
                  onPlan(row.key);
                }}
              >
                <td>
                  <span className="code">{row.p.code}</span>
                  {row.p.closed && <span className="tag">Closed</span>}
                  <div className="pname">{row.p.name}</div>
                </td>
                <td>{row.p.channel}</td>
                <td className="r num">{commonService.formateAmount(row.sell)}</td>
                <td className="r num">{commonService.formateAmount(row.net)}</td>
                <td>
                  <div className="meter">
                    <div
                      className="fill"
                      style={{
                        width: `${percent(row.net)}%`,
                        background: `var(--${row.net >= analysis.h ? "healthy" : "demand"})`,
                      }}
                    />
                    <div
                      className="hurdle"
                      style={{ left: `${percent(analysis.h)}%` }}
                    />
                  </div>
                </td>
                <td className="r num">{commonService.formateRoom(row.rooms)}</td>
                <td>
                  <span className={`badge ${row.cls}`}>{row.act}</span>
                  <div className="reason">{row.reason}</div>
                </td>
                <td>
                  {row.actionable &&
                    (approvedKeys ? (
                      approvedKeys.includes(row.key) && (
                        <span className="badge b-keep">Approved</span>
                      )
                    ) : (
                      <input
                        className="ck"
                        id={commonService.generateGlobalClickTrackerID(
                          "YieldRadar",
                          `action-plan-check-${row.key}`,
                          propertyCode,
                        )}
                        type="checkbox"
                        disabled={api === 0}
                        checked={!excluded.includes(row.key)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => {
                          if (api === 0) return;
                          onToggle(row.key);
                        }}
                        aria-label={`Apply ${row.act} on ${row.p.code} ${row.p.channel}`}
                      />
                    ))}
                </td>
                <td>
                  <button
                    className="drill"
                    id={commonService.generateGlobalClickTrackerID(
                      "YieldRadar",
                      `action-plan-drill-${row.key}`,
                      propertyCode,
                    )}
                    type="button"
                    disabled={api === 0}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (api === 0) return;
                      onPlan(row.key);
                    }}
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-4 text-muted">
                  No rate plans match the selected filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="foot">
        Select any row to drill into that rate plan. Net rate is sell rate after
        commission and package costs. Negotiated and group rates are never
        closed.
      </div>
    </div>
  );
}

function Ranking({ model, selectDate, totalDates, onOpenEvent, close, propertyCode, api }) {
  const records = model.SCR.order.slice(0, 20).map(model.rankRecord);
  const cell = (record, column) => {
    const value = record[column.key];
    if (column.key === "stayDate")
      return (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span>{model.dow(value)} {model.md(value)}</span>
          {record.event && (
            <span
              className="ev-badge"
              id={commonService.generateGlobalClickTrackerID(
                "YieldRadar",
                `ranking-ticket-${record.stayDate}`,
                propertyCode,
              )}
              title={api === 0 ? `Event: ${record.event}` : `Click to view event: ${record.event}`}
              aria-label={api === 0 ? `Event: ${record.event}` : `Click to view event: ${record.event}`}
              style={api === 0 ? { cursor: "default", pointerEvents: "none" } : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (api === 0) return;
                onOpenEvent && onOpenEvent(record.stayDate, record.event);
              }}
            >
              <img
                src={ticket}
                alt="ticket"
                className="iconcolor"
                style={{
                  height: "12px",
                  width: "12px",
                  display: "block",
                  cursor: api === 0 ? "default" : "pointer",
                }}
              />
            </span>
          )}
        </div>
      );
    if (column.key === "diagnosis")
      return (
        <span
          className="badge"
          style={{
            background: `var(${Q[value]?.color || "--healthy"}-bg)`,
            color: `var(${Q[value]?.color || "--healthy"})`,
          }}
        >
          {Q[value]?.label || value}
        </span>
      );
    if (column.key === "revenueAtStake") return commonService.formateAmount(value);
    if (column.key === "rank" || column.key === "daysOut" || column.key === "score" || column.key === "actions")
      return commonService.formateRoom(value);
    return value;
  };
  return (
    <section className="panel full" id="rankPanel" aria-label="Date ranking">
      <div className="tablewrap">
        <table>
          <thead>
            <tr id="rankHead">
              {RANK_COLUMNS.map((column) => (
                <th
                  key={column.key}
                  data-key={column.key}
                  className={column.numeric ? "r" : ""}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody id="rankRows">
            {records.map((record) => (
              <tr
                key={record.stayDate}
                id={commonService.generateGlobalClickTrackerID(
                  "YieldRadar",
                  `ranking-row-${record.stayDate}`,
                  propertyCode,
                )}
                className="clickable"
                style={{ opacity: record.onStrip ? 1 : 0.55 }}
                tabIndex={api === 0 ? -1 : 0}
                onClick={() => {
                  if (api === 0) return;
                  selectDate(record.stayDate, true);
                  close && close();
                }}
                onKeyDown={(event) => {
                  if (api === 0) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    selectDate(record.stayDate, true);
                    close && close();
                  }
                }}
              >
                {RANK_COLUMNS.map((column) => (
                  <td
                    key={column.key}
                    data-key={column.key}
                    className={
                      column.numeric
                        ? "r num"
                        : column.key === "whyFlagged"
                          ? "pname"
                          : ""
                    }
                  >
                    {cell(record, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Reservations({
  analysis,
  model,
  filters,
  setFilters,
  close,
  notify,
  panelRef,
  reservationsLoading = false,
  loading = false,
  propertyCode,
}) {
  const [sort, setSort] = useState({ key: "booked", direction: -1 });
  const [searchInput, setSearchInput] = useState(filters.query || "");

  useEffect(() => {
    setSearchInput(filters.query || "");
  }, [filters.query]);

  useEffect(() => {
    const delay = searchInput === "" ? 0 : 300;
    const timer = setTimeout(() => {
      setFilters((current) =>
        current.query !== searchInput
          ? { ...current, query: searchInput }
          : current,
      );
    }, delay);
    return () => clearTimeout(timer);
  }, [searchInput, setFilters]);

  const all = model.reservations(analysis);
  const active = all.filter((row) => row.status === "Confirmed");
  const average = (key) =>
    active.reduce((total, row) => total + row[key], 0) / (active.length || 1);
  const channels = [...new Set(all.map((row) => row.channel))];
  const plans = [...new Map(all.map((row) => [row.planKey, row])).values()];
  const query = filters.query.trim().toLowerCase();
  const rows = all
    .filter(
      (row) =>
        (filters.status === "all" ||
          row.status ===
          (filters.status === "active" ? "Confirmed" : "Cancelled")) &&
        (!filters.channel || row.channel === filters.channel) &&
        (!filters.plan || row.planKey === filters.plan) &&
        (!query ||
          row.conf.toLowerCase().includes(query) ||
          row.guest.toLowerCase().includes(query)),
    )
    .sort(
      (first, second) =>
        (typeof first[sort.key] === "number"
          ? first[sort.key] - second[sort.key]
          : String(first[sort.key]).localeCompare(String(second[sort.key]))) *
        sort.direction,
    );
  const columns = RES_COLS.map(([key, label, alignment]) => ({
    key,
    label,
    alignment,
  }));
  const changeFilter = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const sortColumn = (key) =>
    setSort((current) => ({
      key,
      direction: current.key === key ? -current.direction : 1,
    }));
  const cell = (row, key) => {
    if (key === "booked" || key === "arrival") return model.md(row[key]);
    if (key === "rate" || key === "net") return commonService.formateAmount(row[key]);
    if (key === "vsToday")
      return row.fixed
        ? "-"
        : `${row.vsToday > 0 ? "+" : ""}${commonService.formateAmount(row.vsToday)}`;
    if (key === "lead" || key === "nights")
      return commonService.formateRoom(row[key]);
    if (key === "plan") return <span className="code">{row.plan}</span>;
    if (key === "status")
      return (
        <>
          <span
            className={`st ${row.status === "Cancelled" ? "st-cx" : "st-ok"}`}
          >
            {row.status}
          </span>
          {row.cancelDate && (
            <div className="pname">{model.md(row.cancelDate)}</div>
          )}
        </>
      );
    return row[key];
  };
  const exportCsv = () => {
    const fields = {
      confirmation_no: "conf",
      guest_name: "guest",
      booking_date: "booked",
      lead_days: "lead",
      arrival_date: "arrival",
      departure_date: "depart",
      nights: "nights",
      room_type: "room",
      rate_plan_code: "plan",
      channel: "channel",
      market_segment: "segment",
      nightly_rate: "rate",
      net_rate: "net",
      vs_today: "vsToday",
      status: "status",
      cancel_date: "cancelDate",
    };
    const quote = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const csv = [
      Object.keys(fields).map(quote).join(","),
      ...rows.map((row) =>
        Object.values(fields)
          .map((key) => quote(key === "vsToday" && row.fixed ? "" : row[key]))
          .join(","),
      ),
    ].join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `reservations_${propertyCode || PROPERTY || "property"}_${analysis?.s?.d || "date"}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    // notify("CSV exported");
  };
  return (
    <section
      className="panel full"
      id="resPanel"
      ref={panelRef}
      aria-label="Reservations"
    >
      <div className="section">
        <div className="kpis">
          <Kpi
            label="Rooms on the books"
            value={commonService.formateRoom(active.length)}
            detail={`${commonService.formateRoom(all.length - active.length)} cancelled`}
          />
          <Kpi
            label="ADR"
            value={commonService.formateAmount(average("rate"))}
            detail={`Net ADR ${commonService.formateAmount(average("net"))}`}
          />
          <Kpi
            label="Avg lead time"
            value={`${commonService.formateRoom(Math.round(average("lead")))} days`}
            detail="Booking to arrival"
          />
          <Kpi
            label="Sold below today's rate"
            value={commonService.formateRoom(active.filter((row) => row.vsToday < 0).length)}
            detail={`${commonService.formateRoom(Math.round((active.filter((row) => row.vsToday < 0).length / (active.length || 1)) * 100))}% of rooms`}
          />
        </div>
      </div>
      <div className="filters">
        <input
          type="search"
          id={commonService.generateGlobalClickTrackerID(
            "YieldRadar",
            "res-search-input",
            propertyCode,
          )}
          placeholder="Search confirmation or guest"
          aria-label="Search reservations"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <select
          id={commonService.generateGlobalClickTrackerID(
            "YieldRadar",
            "res-channel-select",
            propertyCode,
          )}
          aria-label="Channel"
          value={filters.channel}
          onChange={(event) => changeFilter("channel", event.target.value)}
        >
          <option value="">All channels</option>
          {channels.map((channel) => (
            <option key={channel}>{channel}</option>
          ))}
        </select>
        <select
          id={commonService.generateGlobalClickTrackerID(
            "YieldRadar",
            "res-plan-select",
            propertyCode,
          )}
          aria-label="Rate plan"
          value={filters.plan}
          onChange={(event) => changeFilter("plan", event.target.value)}
        >
          <option value="">All rate plans</option>
          {plans.map((plan) => (
            <option key={plan.planKey} value={plan.planKey}>
              {plan.plan} ({plan.channel})
            </option>
          ))}
        </select>
        <select
          id={commonService.generateGlobalClickTrackerID(
            "YieldRadar",
            "res-status-select",
            propertyCode,
          )}
          aria-label="Status"
          value={filters.status}
          onChange={(event) => changeFilter("status", event.target.value)}
        >
          <option value="active">Active only</option>
          <option value="cx">Cancelled only</option>
          <option value="all">All statuses</option>
        </select>
        <span className="spacer" />
        <span className="pname">
          {commonService.formateRoom(rows.length)} of {commonService.formateRoom(all.length)} reservations
        </span>
        <button
          className="approve"
          id={commonService.generateGlobalClickTrackerID(
            "YieldRadar",
            "res-export-csv-btn",
            propertyCode,
          )}
          type="button"
          onClick={exportCsv}
        >
          Export CSV
        </button>
      </div>
      <div className="restable">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  id={commonService.generateGlobalClickTrackerID(
                    "YieldRadar",
                    `res-sort-${column.key}`,
                    propertyCode,
                  )}
                  className={column.alignment || ""}
                  aria-sort={
                    sort.key === column.key
                      ? sort.direction > 0
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                  tabIndex={0}
                  onClick={() => sortColumn(column.key)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      sortColumn(column.key);
                    }
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${row.conf}-${index}`}
                className={row.status === "Cancelled" ? "cx" : ""}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`${column.alignment || ""} ${["conf", "booked", "arrival", "lead", "nights", "rate", "net", "vsToday"].includes(column.key) ? "num" : ""} ${column.key === "vsToday" ? (row.vsToday < 0 ? "delta-down" : row.vsToday > 0 ? "delta-up" : "") : ""}`}
                  >
                    {cell(row, column.key)}
                  </td>
                ))}
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  {reservationsLoading
                    ? "Loading reservations in background..."
                    : "No reservations found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* <div className="foot">
        Guest names are masked sample data. "vs. today" compares the booked rate
        with today's sell rate for the same plan; negative means the room was
        sold below today's price.
      </div> */}
    </section>
  );
}

function Campaigns({ model, planned, setPlanned, copy, notify, propertyCode, onOpenEvent, api }) {
  const campaigns = model.buildCampaigns();
  const horizonText = model.horizonText || "90+ days";
  return (
    <section
      className="panel full"
      id="promoPanel"
      aria-label="Promotion campaigns"
    >
      <div className="rhead">
        <div>
          <h2>Promotion campaigns to design</h2>
          <p>
            Built from soft dates across the next {horizonText}. Compressed dates are
            blacked out so promotions never discount rooms that will sell
            anyway.
          </p>
        </div>
      </div>
      <div className="campaigns">
        {campaigns.map((campaign) => {
          const key = `${campaign.owner}|${campaign.type}|${campaign.dates.join(",")}`;
          const brief = {
            owner: campaign.owner,
            promo_type: campaign.type,
            name: campaign.name,
            stay_dates: campaign.dates,
            blackout_dates: campaign.blackout,
            discount_pct: Math.round(campaign.disc * 100),
            fence: campaign.fence,
            booking_window: {
              start: campaign.bookStart,
              end: campaign.bookEnd,
            },
            target_rooms: campaign.targetRooms,
            min_net_minus_hurdle: campaign.margin,
            rationale: campaign.why,
          };
          const hasEvents = !!(campaign.events || campaign.type === "EVENT");
          const eventText = campaign.events || campaign.name;
          const firstDate = campaign.dates?.[0];

          return (
            <article
              key={key}
              className={`camp ${PROMO_OWNERS[campaign.owner]?.cls || "o-channel"}`}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <span className="owner">{campaign.owner}</span>{" "}
                  <span className="pname">{PROMO_TYPE_LABEL[campaign.type] || ""}</span>
                </div>
                {hasEvents && (
                  <span
                    className="ev-badge"
                    id={commonService.generateGlobalClickTrackerID(
                      "YieldRadar",
                      `campaign-badge-event-${firstDate}`,
                      propertyCode,
                    )}
                    title={api === 0 ? `Events: ${eventText}` : `View Events: ${eventText} (Click to view)`}
                    aria-label={`View Events: ${eventText}`}
                    style={api === 0 ? { cursor: "default", pointerEvents: "none" } : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (api === 0) return;
                      if (onOpenEvent && firstDate) {
                        onOpenEvent(firstDate, eventText);
                      }
                    }}
                  >
                    <img
                      src={ticket}
                      alt="ticket"
                      className="iconcolor"
                      style={{
                        width: "12px",
                        height: "12px",
                        display: "block",
                        cursor: "pointer",
                      }}
                    />
                  </span>
                )}
              </div>
              <h3>{campaign.name}</h3>
              <div className="dchips">
                {(campaign.dates || []).map((date) => (
                  <span key={date}>
                    {model.dow(date)} {model.md(date)}
                  </span>
                ))}
                {(campaign.blackout || []).map((date) => (
                  <span
                    key={date}
                    className="bo"
                    title="Blacked out: compressed date"
                  >
                    {model.dow(date)} {model.md(date)}
                  </span>
                ))}
              </div>
              <dl>
                <dt>Offer</dt>
                <dd>
                  {campaign.disc
                    ? `${Math.round(campaign.disc * 100)}% off BAR`
                    : "Value-add, no rate cut"}
                </dd>
                <dt>Fence</dt>
                <dd>{campaign.fence || "—"}</dd>
                <dt>Book between</dt>
                <dd>
                  {campaign.bookStart ? model.md(campaign.bookStart) : "—"} and{" "}
                  {campaign.bookEnd ? model.md(campaign.bookEnd) : "—"}
                </dd>
                <dt>Target</dt>
                <dd>{commonService.formateRoom(campaign.targetRooms || 0)} rooms behind STLY</dd>
                <dt>Net vs. hurdle</dt>
                <dd
                  className={(campaign.margin || 0) >= 0 ? "delta-up" : "delta-down"}
                >
                  {(campaign.margin || 0) >= 0 ? "+" : ""}
                  {commonService.formateAmount(campaign.margin || 0)} at the tightest date
                </dd>
                {(campaign.blackout || []).length > 0 && (
                  <>
                    <dt>Blackout</dt>
                    <dd>
                      {(campaign.blackout || []).map(model.md).join(", ")} (compressed)
                    </dd>
                  </>
                )}
              </dl>
              <ul>
                {(campaign.why || []).map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
              <div className="btns">
                <button
                  className="ghost"
                  id={commonService.generateGlobalClickTrackerID(
                    "YieldRadar",
                    `campaign-add-btn-${campaign.owner}-${campaign.type}`,
                    propertyCode,
                  )}
                  type="button"
                  disabled={api === 0 || !!planned[key]}
                  onClick={() => {
                    if (api === 0) return;
                    setPlanned((current) => ({ ...current, [key]: brief }));
                    notify(`${campaign.name} added to the campaign plan`);
                  }}
                >
                  {planned[key] ? "Added to plan" : "Add to campaign plan"}
                </button>
                <button
                  className="ghost"
                  id={commonService.generateGlobalClickTrackerID(
                    "YieldRadar",
                    `campaign-copy-btn-${campaign.owner}-${campaign.type}`,
                    propertyCode,
                  )}
                  type="button"
                  disabled={api === 0}
                  onClick={() => {
                    if (api === 0) return;
                    copy(brief);
                  }}
                >
                  Copy brief
                </button>
              </div>
            </article>
          );
        })}
        {!campaigns.length && <p className="pname">No soft dates in range.</p>}
      </div>
    </section>
  );
}

function Drawer({ row, analysis, model, close, openReservations, copy, propertyCode, api }) {
  const drawerRef = useRef(null);
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const previous = document.activeElement;
    const drawer = drawerRef.current;
    drawer?.querySelector("button")?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") close();
      if (event.key !== "Tab") return;
      const focusable = [
        ...drawer.querySelectorAll('button, input, select, [tabindex="0"]'),
      ].filter((element) => !element.disabled);
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
      previous?.focus();
    };
  }, [close]);
  const drawerHtml = React.useMemo(() => {
    return model.renderDrawer(analysis, row);
  }, [model, analysis, row]);

  return (
    <>
      <div
        className="scrim on"
        id={commonService.generateGlobalClickTrackerID(
          "YieldRadar",
          "drawer-scrim",
          propertyCode,
        )}
        onClick={close}
      />
      <aside
        className="drawer on"
        id="drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dTitle"
      >
        <div className="dhead">
          <div>
            <div className="sub">
              {model.longDate(analysis.s.d)}, {commonService.formateRoom(analysis.s.dba)} days out,{" "}
              {Q[analysis.q]?.label || ""}
            </div>
            <h2 id="dTitle">
              {row.p.code} on {row.p.channel}
            </h2>
            <div>
              <span className={`badge ${row.cls}`}>{row.act}</span>{" "}
              <span className="sub">{row.reason}</span>
            </div>
          </div>
          <button
            className="xbtn"
            id={commonService.generateGlobalClickTrackerID(
              "YieldRadar",
              "drawer-close-btn",
              propertyCode,
            )}
            type="button"
            aria-label="Close rate plan details"
            onClick={close}
          >
            &times;
          </button>
        </div>
        <div className="dbody">
          <DetailContent
            html={drawerHtml}
            onReservations={() => openReservations(row.key)}
            onCopyPlan={() => copy(model.planAction(analysis, row))}
            propertyCode={propertyCode}
            disabled={api === 0}
          />
        </div>
      </aside>
    </>
  );
}

function Quadrant({ model, selectedDate, selectDate, openReservations, propertyCode }) {
  const bounds = { left: 34, right: 330, top: 12, bottom: 220 };
  const xPosition = (value) =>
    bounds.left +
    ((Math.min(1.5, Math.max(0.6, value)) - 0.6) / 0.9) *
    (bounds.right - bounds.left);
  const yPosition = (value) =>
    bounds.top +
    (1 - (Math.min(1.12, Math.max(0.88, value)) - 0.88) / 0.24) *
    (bounds.bottom - bounds.top);
  const centerX = xPosition(1),
    centerY = yPosition(1);
  const points = model.SCR.list
    .filter((item) => item.a.s.d !== selectedDate)
    .concat(model.SCR.list.filter((item) => item.a.s.d === selectedDate));
  return (
    <svg
      id="quad"
      viewBox="0 0 340 250"
      role="img"
      aria-label="Pace and ADR quadrant chart"
    >
      {Object.entries({
        healthy: [
          centerX,
          bounds.top,
          bounds.right - centerX,
          centerY - bounds.top,
        ],
        under: [
          centerX,
          centerY,
          bounds.right - centerX,
          bounds.bottom - centerY,
        ],
        resist: [
          bounds.left,
          bounds.top,
          centerX - bounds.left,
          centerY - bounds.top,
        ],
        demand: [
          bounds.left,
          centerY,
          centerX - bounds.left,
          bounds.bottom - centerY,
        ],
      }).map(([key, rectangle]) => (
        <rect
          key={key}
          x={rectangle[0]}
          y={rectangle[1]}
          width={rectangle[2]}
          height={rectangle[3]}
          fill={`var(${Q[key].color})`}
          opacity=".08"
        />
      ))}
      <rect
        x={bounds.left}
        y={bounds.top}
        width={bounds.right - bounds.left}
        height={bounds.bottom - bounds.top}
        fill="none"
        stroke="var(--line)"
      />
      <line
        x1={centerX}
        x2={centerX}
        y1={bounds.top}
        y2={bounds.bottom}
        stroke="var(--muted)"
        strokeDasharray="3 3"
      />
      <line
        x1={bounds.left}
        x2={bounds.right}
        y1={centerY}
        y2={centerY}
        stroke="var(--muted)"
        strokeDasharray="3 3"
      />
      {Object.entries(Q).map(([key, quadrant]) => (
        <text
          key={key}
          x={["healthy", "under"].includes(key) ? 324 : 40}
          y={["healthy", "resist"].includes(key) ? 26 : 213}
          textAnchor={["healthy", "under"].includes(key) ? "end" : "start"}
          fontSize="10.5"
          fontWeight="600"
          fill={`var(${quadrant.color})`}
        >
          {quadrant.label}
        </text>
      ))}
      {[0.7, 1, 1.3].map((value) => (
        <text
          key={value}
          x={xPosition(value)}
          y="234"
          textAnchor="middle"
          fontSize="10"
          fill="var(--muted)"
        >
          {Math.round((value - 1) * 100)}%
        </text>
      ))}
      {[0.95, 1, 1.05].map((value) => (
        <text
          key={value}
          x="29"
          y={yPosition(value) + 3}
          textAnchor="end"
          fontSize="10"
          fill="var(--muted)"
        >
          {Math.round((value - 1) * 100)}%
        </text>
      ))}
      <text
        x="182"
        y="247"
        textAnchor="middle"
        fontSize="10.5"
        fill="var(--muted)"
      >
        Pace vs. STLY
      </text>
      <text
        transform="translate(10 116) rotate(-90)"
        textAnchor="middle"
        fontSize="10.5"
        fill="var(--muted)"
      >
        ADR vs. STLY
      </text>
      {points.map((item) => {
        const selected = item.a.s.d === selectedDate;
        const pointX = xPosition(model.paceIndex(item.a.s)),
          pointY = yPosition(model.adrIndex(item.a.s));
        const open = () => {
          selectDate(item.a.s.d);
          openReservations();
        };
        return (
          <g key={item.a.s.d}>
            {selected && (
              <circle
                cx={pointX}
                cy={pointY}
                r="11"
                fill={`var(${Q[item.a.q].color})`}
                opacity=".2"
              />
            )}
            <circle
              className="qdot"
              id={commonService.generateGlobalClickTrackerID(
                "YieldRadar",
                `quadrant-dot-${item.a.s.d}`,
                propertyCode,
              )}
              cx={pointX}
              cy={pointY}
              r={selected ? 6.5 : item.onStrip ? 5 : 3.2}
              fill={`var(${Q[item.a.q].color})`}
              opacity={selected ? 1 : item.onStrip ? 0.85 : 0.35}
              stroke={selected ? "var(--panel)" : undefined}
              strokeWidth="2"
              tabIndex={0}
              role="button"
              aria-label={`${model.md(item.a.s.d)}: ${Q[item.a.q]?.label || ""}, rank ${commonService.formateRoom(item.rank)}`}
              onClick={open}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  open();
                }
              }}
            >
              <title>
                {model.md(item.a.s.d)}: {Q[item.a.q]?.label || ""}, rank {commonService.formateRoom(item.rank)}
              </title>
            </circle>
            {selected && (
              <text
                x={pointX + (pointX > 250 ? -12 : 12)}
                y={pointY < 42 ? pointY + 20 : pointY - 10}
                textAnchor={pointX > 250 ? "end" : "start"}
                fontSize="11.5"
                fontWeight="700"
                fill="var(--text)"
              >
                {model.md(item.a.s.d)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function TestSignals({
  signals,
  selectedIndex,
  overrides,
  onSignalChange,
  onReset,
  model,
  propertyCode,
  api,
}) {
  const [sliderValues, setSliderValues] = useState({
    otb: signals.otb,
    fcst: signals.fcst,
    adr: signals.adr,
  });
  const pendingValuesRef = useRef(sliderValues);
  const timerRef = useRef(null);
  const isInteractingRef = useRef(false);
  const lastIndexRef = useRef(selectedIndex);

  useEffect(() => {
    const isNewIndex = lastIndexRef.current !== selectedIndex;
    lastIndexRef.current = selectedIndex;
    if (isNewIndex || !isInteractingRef.current) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const current = {
        otb: signals.otb,
        fcst: signals.fcst,
        adr: signals.adr,
      };
      setSliderValues(current);
      pendingValuesRef.current = current;
    }
  }, [signals.otb, signals.fcst, signals.adr, selectedIndex]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleSliderChange = (key, rawVal) => {
    if (api === 0) return;
    isInteractingRef.current = true;
    const num = Number(rawVal);
    const next = {
      ...pendingValuesRef.current,
      [key]: num,
    };
    if (key === "otb" && num > next.fcst) {
      next.fcst = num;
    }
    pendingValuesRef.current = next;
    setSliderValues(next);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onSignalChange(pendingValuesRef.current);
    }, 200);
  };

  const handleRelease = () => {
    if (api === 0) return;
    isInteractingRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onSignalChange(pendingValuesRef.current);
  };

  const handleReset = () => {
    if (api === 0) return;
    isInteractingRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onReset();
  };

  const signalConfigs = [
    {
      key: "otb",
      label: "Occupancy on the books",
      min: 5,
      max: 100,
    },
    { key: "fcst", label: "Forecast occupancy", min: 5, max: 100 },
    {
      key: "adr",
      label: "Current ADR",
      min: Math.round(signals.stlyAdr * 0.8),
      max: Math.round(signals.stlyAdr * 1.2),
    },
  ];

  return (
    <div className="sliders">
      <h3>Test the signals</h3>
      {signalConfigs.map((signal) => {
        const sliderId = commonService.generateGlobalClickTrackerID(
          "YieldRadar",
          `signal-slider-${signal.key}`,
          propertyCode,
        );
        return (
          <div className="srow" key={signal.key}>
            <label htmlFor={sliderId}>{signal.label}</label>
            <output className="num">
              {signal.key === "adr"
                ? commonService.formateAmount(sliderValues.adr)
                : model.P1(sliderValues[signal.key])}
            </output>
            <input
              type="range"
              id={sliderId}
              min={signal.min}
              max={signal.max}
              step="1"
              value={sliderValues[signal.key] ?? signals[signal.key]}
              disabled={api === 0}
              onChange={(event) =>
                handleSliderChange(signal.key, event.target.value)
              }
              onPointerDown={() => {
                if (api === 0) return;
                isInteractingRef.current = true;
              }}
              onPointerUp={handleRelease}
              onMouseUp={handleRelease}
              onTouchEnd={handleRelease}
              onKeyUp={handleRelease}
            />
          </div>
        );
      })}
      {(overrides[selectedIndex] || overrides[signals.d]) && (
        <button
          className="reset"
          type="button"
          id={commonService.generateGlobalClickTrackerID(
            "YieldRadar",
            "reset-to-actuals",
            propertyCode,
          )}
          disabled={api === 0}
          onClick={handleReset}
        >
          Reset to actuals
        </button>
      )}
    </div>
  );
}

export default function RadarMain({
  propertyName,
  asOfDate,
  propertyCode,
  capacity: propCapacity,
  startDate,
  endDate,
  dailyData,
  reservationData,
  reservationsLoading = false,
  loading = false,
  headerControls,
  api,
  combinedTableId,
  onSendMail,
  onInfo,
  eventsList = [],
  getEventList,
}) {
  const propertyDetailFromRTK = useSelector(
    (store) => store?.headerProperty?.propertyDetail,
  );
  const rtkCapacity = Number(propertyDetailFromRTK?.revenuconfig?.[0]?.totalrooms);
  const capacity = Number(propCapacity) || (rtkCapacity > 0 ? rtkCapacity : (CONFIG.capacity || 126));
  const eventsListRef = useRef(eventsList);
  useEffect(() => {
    if (eventsList && eventsList.length) {
      eventsListRef.current = eventsList;
    }
  }, [eventsList]);

  const [showTicketModal, setShowTicketModal] = useState(false);
  const selectedEventDateRef = useRef(null);
  const eventListPropsRef = useRef([]);

  const loadEventsIfEmpty = useCallback(async (force = false) => {
    if (api === 0) return eventsListRef.current || [];
    if (!force && eventsListRef.current && eventsListRef.current.length > 0) {
      return eventsListRef.current;
    }
    try {
      const propId = propertyDetailFromRTK?.propertyid;
      if (!propId) return eventsListRef.current || [];
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASE_API_URL.RevenueManagement + API_URL.EVENTS.GET_LIST,
          body: {
            pageNum: 1,
            pageSize: 100,
            sortColumn: "",
            sortDirection: "asc",
            searchValue: "",
            propertyid: parseInt(propId),
            startdate: startDate ? commonService.getDateInDBFormat(startDate) : null,
            enddate: endDate ? commonService.getDateInDBFormat(endDate) : null,
            eventname: "",
            eventtypeterm: "",
            yearterm: "",
          },
        },
        false,
      );
      const evResp = apiResponse(false, res);
      const raw = evResp?.data?.data || evResp?.data || [];
      const formatted = formatEventsList(raw);
      if (formatted && formatted.length) {
        eventsListRef.current = formatted;
      }
      return eventsListRef.current;
    } catch (e) {
      return eventsListRef.current || [];
    }
  }, [propertyDetailFromRTK?.propertyid, startDate, endDate, api]);

  const openEventModal = useCallback(async (dateStr, evName) => {
    if (api === 0) return;
    selectedEventDateRef.current = dateStr;
    let currentEvents = eventsListRef.current;
    if (!currentEvents || currentEvents.length === 0) {
      currentEvents = await loadEventsIfEmpty();
    }
    let matched = [];
    if (dateStr && currentEvents && currentEvents.length > 0) {
      const checkYMD = toCalendarYMD(dateStr);
      matched = currentEvents.filter((x) => {
        const sYMD = toCalendarYMD(x?.startDate || x?.startdate);
        const eYMD = toCalendarYMD(x?.endDate || x?.enddate || x?.startDate || x?.startdate) || sYMD;
        return (!sYMD || checkYMD >= sYMD) && (!eYMD || checkYMD <= eYMD);
      });
    }
    if ((!matched || matched.length === 0) && evName && currentEvents && currentEvents.length > 0) {
      const evLower = String(evName).toLowerCase();
      matched = currentEvents.filter((x) => {
        const xName = String(x?.name || x?.eventname || "").toLowerCase();
        return xName && (evLower.includes(xName) || xName.includes(evLower));
      });
    }
    if (!matched || matched.length === 0) {
      const names = evName
        ? String(evName).split(",").map((s) => s.trim()).filter(Boolean)
        : ["Event"];
      matched = names.map((n, i) => ({
        id: i,
        name: n,
        detail: n,
        startDate: dateStr,
        endDate: dateStr,
      }));
    }
    eventListPropsRef.current = matched;
    setShowTicketModal(true);
  }, [loadEventsIfEmpty, api]);

  const dates = React.useMemo(() => {
    if (dailyData && dailyData.length) {
      const built = buildDatesFromDaily(dailyData, asOfDate, capacity);
      if (built && built.length > 0) return built;
    }
    return [];
  }, [dailyData, asOfDate, capacity]);

  const [root, setRoot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    () => dates?.[0]?.d || toCalendarYMD(asOfDate) || moment().format("YYYY-MM-DD"),
  );
  const [tab, setTab] = useState("actions");
  const [overrides, setOverrides] = useState({});
  const [excluded, setExcluded] = useState({});
  const [approved, setApproved] = useState({});
  const [drawerKey, setDrawerKey] = useState(null);
  const [showReservations, setShowReservations] = useState(false);
  const [showRanking, setShowRanking] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    channel: "",
    plan: "",
    status: "active",
  });
  const [planned, setPlanned] = useState({});
  const reservationsRef = useRef(null),
    stripRef = useRef(null),
    tabsRef = useRef(null),
    leftPanelRef = useRef(null);
  const [leftHeight, setLeftHeight] = useState(null);

  useEffect(() => {
    if (dates && dates.length > 0) {
      const exists = dates.some((d) => d.d === selectedDate);
      if (!exists) {
        setSelectedDate(dates[0].d);
      }
    }
  }, [dates, selectedDate]);

  const foundIndex = dates.findIndex((date) => date.d === selectedDate);
  const selectedIndex = foundIndex >= 0 ? foundIndex : 0;

  useEffect(() => {
    if (!leftPanelRef.current) return;
    const updateHeight = () => {
      if (leftPanelRef.current) {
        if (typeof window !== "undefined" && window.innerWidth > 980) {
          const h = leftPanelRef.current.offsetHeight;
          if (h > 0) {
            setLeftHeight(h);
            if (root) {
              root.style.setProperty("--left-panel-height", `${h}px`);
            }
          }
        } else {
          setLeftHeight(null);
          if (root) {
            root.style.removeProperty("--left-panel-height");
          }
        }
      }
    };

    updateHeight();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        updateHeight();
      });
      ro.observe(leftPanelRef.current);
    }

    window.addEventListener("resize", updateHeight);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [root, selectedIndex, overrides]);
  const model = React.useMemo(() => {
    return createRadarModel(overrides, root, dates, asOfDate, reservationData, capacity, propertyCode);
  }, [overrides, root, dates, asOfDate, reservationData, capacity, propertyCode]);
  const analysis = React.useMemo(() => {
    return model.analyse(selectedIndex);
  }, [model, selectedIndex]);
  const signals = analysis.s,
    rank = model.SCR.list[selectedIndex] || model.SCR.list[0] || { score: 0, rank: 1, onStrip: true };
  const drawerRow =
    drawerKey == null
      ? null
      : analysis.rows.find((row) => row.key === drawerKey);
  const uncheckedKeys = excluded[selectedDate] || [];
  const actionable = analysis.rows.filter(
    (row) => row.actionable && !uncheckedKeys.includes(row.key),
  );

  const stripDates = React.useMemo(() => {
    const sourceList =
      model?.SCR?.list && model.SCR.list.length > 0
        ? model.SCR.list
        : model?.SCR?.order || [];
    return sourceList
      .filter((item) => item.onStrip || item.a?.s?.d === selectedDate)
      .slice()
      .sort((a, b) => {
        const da = a.a?.s?.d || "";
        const db = b.a?.s?.d || "";
        return da.localeCompare(db);
      });
  }, [model, selectedDate]);

  const content = React.useMemo(() => {
    if (!root || tab === "actions" || !analysis || !model) return "";
    const tabRenderers = {
      pace: model.renderPace,
      mix: model.renderMix,
      trace: model.renderTrace,
      promo: model.renderPromoTab,
      spec: model.renderSpec,
    };
    const renderer = tabRenderers[tab];
    return renderer ? renderer(analysis) : "";
  }, [root, tab, analysis, model]);


  useEffect(() => {
    stripRef.current
      ?.querySelector('[aria-pressed="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
  }, [selectedDate]);

  const notify = (text) => successToastMessage(text);

  const copy = async (value) => {
    if (api === 0) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
      successToastMessage("JSON copied");
    } catch {
      errorToastMessage("Copy not available here; select the text instead");
    }
  };
  const selectDate = (date, scrollTop = false) => {
    if (api === 0) return;
    setSelectedDate(date);
    setDrawerKey(null);
    if (scrollTop) {
      root?.scrollIntoView({
        behavior: "auto",
      });
    }
  };
  const openReservations = (plan = "") => {
    if (api === 0) return;
    setFilters((current) => ({ ...current, plan, channel: "" }));
    setDrawerKey(null);
    setShowReservations(true);
  };
  const changeSignal = (keyOrObj, value) => {
    if (api === 0) return;
    let next;
    if (typeof keyOrObj === "object" && keyOrObj !== null) {
      next = {
        otb: signals.otb,
        fcst: signals.fcst,
        adr: signals.adr,
        ...keyOrObj,
      };
    } else {
      next = {
        otb: signals.otb,
        fcst: signals.fcst,
        adr: signals.adr,
        [keyOrObj]: Number(value),
      };
    }
    React.startTransition(() => {
      setOverrides((current) => ({
        ...current,
        [selectedIndex]: next,
        [selectedDate]: next,
      }));
      setApproved((current) => ({ ...current, [selectedDate]: undefined }));
      setExcluded((current) => ({ ...current, [selectedDate]: [] }));
    });
  };
  const reset = () => {
    if (api === 0) return;
    React.startTransition(() => {
      setOverrides((current) => {
        const next = { ...current };
        delete next[selectedIndex];
        delete next[selectedDate];
        return next;
      });
      setApproved((current) => ({ ...current, [selectedDate]: undefined }));
      setExcluded((current) => ({ ...current, [selectedDate]: [] }));
    });
  };
  const toggle = (key) => {
    if (api === 0) return;
    setExcluded((current) => ({
      ...current,
      [selectedDate]: uncheckedKeys.includes(key)
        ? uncheckedKeys.filter((value) => value !== key)
        : [...uncheckedKeys, key],
    }));
  };
  const approve = () => {
    if (api === 0) return;
    setApproved((current) => ({
      ...current,
      [selectedDate]: actionable.map((row) => row.key),
    }));
    notify(
      `${commonService.formateRoom(actionable.length)} change${actionable.length === 1 ? "" : "s"} approved for ${model.md(selectedDate)} and queued for the channel manager`,
    );
  };

  const occupancyDelta = signals.otb - signals.stly,
    adrDelta = signals.adr - signals.stlyAdr;

  if (!dates || dates.length === 0) {
    if (loading) {
      return (
        <div
          className="d-flex justify-content-center align-items-center w-100"
          style={{ minHeight: "60vh" }}
        >
          <Loader />
        </div>
      );
    }
    return (
      <div
        className={`YieldRadar ${api === 0 ? "yieldradar-disabled" : ""}`}
        ref={setRoot}
        style={api === 0 ? { pointerEvents: "none", userSelect: "none" } : undefined}
      >
        <div className="wrap">
          <header className="top">
            <div className="d-flex align-items-center flex-wrap gap-3">
              {headerControls}
            </div>
          </header>
          <div
            className="d-flex flex-column justify-content-center align-items-center w-100 p-5"
            style={{ minHeight: "350px", textAlign: "center" }}
          >
            <p className="text-muted" style={{ fontSize: "16px", fontWeight: 500 }}>
              No yield radar data available for the selected date range.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`YieldRadar ${api === 0 ? "yieldradar-disabled" : ""}`}
      ref={setRoot}
      style={api === 0 ? { pointerEvents: "none", userSelect: "none" } : undefined}
    >
      <div className="wrap" inert={drawerRow ? true : undefined}>
        <header className="top">
          <div className="d-flex align-items-center flex-wrap gap-3">
            {headerControls}
          </div>
          {/* <div className="meta">
            <strong>{propertyCode || PROPERTY}</strong>, {capacity} rooms<br />
            As of {model.dow(asOfDate || AS_OF)}, {model.md(asOfDate || AS_OF)}, {formatCalendarDate(asOfDate || AS_OF, "YYYY")}
          </div> */}
        </header>
        {/* <nav className="crumbs" aria-label="Drill-down path">
          <button
            type="button"
            onClick={() => {
              setDrawerKey(null);
              stripRef.current?.scrollIntoView({ block: "start" });
            }}
          >
            All stay dates
          </button>
          <span className="sep">&gt;</span>
          <span className="cur">
            {model.md(selectedDate)},{" "}
            {drawerRow
              ? `${drawerRow.p.code} on ${drawerRow.p.channel}`
              : TABS[tab]}
          </span>
        </nav> */}
        <nav
          className="strip"
          id="strip"
          ref={stripRef}
          aria-label="Stay dates"
        >
          {stripDates?.map((item) => (
              <button
                key={item.a.s.d}
                id={commonService.generateGlobalClickTrackerID(
                  "YieldRadar",
                  `strip-date-${item.a.s.d}`,
                  propertyCode,
                )}
                className={`chip ${item.onStrip ? "" : "off"}`}
                type="button"
                aria-pressed={item.a.s.d === selectedDate}
                disabled={api === 0}
                style={{
                  "--c": `var(${Q[item.a.q]?.color || "--healthy"})`,
                  ...(api === 0 ? { cursor: "default" } : {}),
                }}
                aria-label={`${model.md(item.a.s.d)}, ${commonService.formateRoom(item.a.s.dba)} days out, ${Q[item.a.q]?.label || ""}, rank ${commonService.formateRoom(item.rank)}, score ${commonService.formateRoom(item.score)}, ${commonService.formateRoom(item.actions)} ${item.actions === 1 ? "action" : "actions"}${item.a.s.ev ? `, Event: ${item.a.s.ev}` : ""}`}
                onClick={() => {
                  if (api === 0) return;
                  selectDate(item.a.s.d);
                }}
              >
                <div className="chip-top">
                  <span className="rk">
                    {item.onStrip
                      ? `#${commonService.formateRoom(item.rank)} · ${commonService.formateRoom(item.score)}`
                      : `#${commonService.formateRoom(item.rank)} · ${commonService.formateRoom(item.score)} (off)`}
                  </span>
                  {item.a.s.ev && (
                    <span
                      className="ev-badge"
                      id={commonService.generateGlobalClickTrackerID(
                        "YieldRadar",
                        `strip-event-${item.a.s.d}`,
                        propertyCode,
                      )}
                      title={api === 0 ? `Event: ${item.a.s.ev}` : `Event: ${item.a.s.ev} (Click to view)`}
                      aria-label={`Event: ${item.a.s.ev}`}
                      style={api === 0 ? { cursor: "default", pointerEvents: "none" } : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (api === 0) return;
                        openEventModal(item.a.s.d, item.a.s.ev);
                      }}
                    >
                      <img
                        src={ticket}
                        alt="ticket"
                        className="iconcolor"
                        style={{
                          width: "12px",
                          height: "12px",
                          display: "block",
                          cursor: api === 0 ? "default" : "pointer",
                        }}
                      />
                    </span>
                  )}
                </div>
                <span className="dow">{model.dow(item.a.s.d)}</span>
                <span className="dt">{model.md(item.a.s.d)}</span>
                <span className="out">{commonService.formateRoom(item.a.s.dba)} days out</span>
                <span
                  className={`act-count ${item.actions > 0 ? `has-actions q-${item.a.q}` : "zero"}`}
                  title={`${commonService.formateRoom(item.actions)} ${item.actions === 1 ? "action" : "actions"}${Q[item.a.q] ? ` · ${Q[item.a.q].label}` : ""}`}
                >
                  {commonService.formateRoom(item.actions)} {item.actions === 1 ? "action" : "actions"}
                </span>
                <span className="bar" />
              </button>
            ))}
        </nav>
        <div className="grid">
          <section className="panel left-panel" ref={leftPanelRef} aria-label="Diagnosis">
            <div
              className="diag-head"
              style={{ "--c": `var(${Q[analysis.q]?.color || "--healthy"})` }}
            >
              <div className="diag-date">
                {model.longDate(selectedDate)}, {commonService.formateRoom(signals.dba)} days out
                {signals.ev && (
                  <span
                    id={commonService.generateGlobalClickTrackerID(
                      "YieldRadar",
                      "diag-event-ticket",
                      propertyCode,
                    )}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      marginLeft: "6px",
                      cursor: api === 0 ? "default" : "pointer",
                      ...(api === 0 ? { pointerEvents: "none" } : {}),
                    }}
                    title={api === 0 ? `Event: ${signals.ev}` : `View Event: ${signals.ev}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (api === 0) return;
                      openEventModal(selectedDate, signals.ev);
                    }}
                  >
                    <span>, {signals.ev}</span>
                    <img
                      src={ticket}
                      alt="ticket"
                      className="iconcolor"
                      style={{
                        height: "12px",
                        width: "12px",
                        display: "inline-block",
                        cursor: api === 0 ? "default" : "pointer",
                      }}
                    />
                  </span>
                )}
              </div>
              <div className="diag-title">
                <span className="dot" />
                <h2>{Q[analysis.q]?.label || ""}</h2>
              </div>
              <p className="diag-sub">{Q[analysis.q]?.sub || ""}</p>
              <div className="diag-date">
                Confidence: {analysis.conf === "HIGH" ? "High" : "Medium"}
              </div>
              <div className="scoreline">
                Attention score <b>{commonService.formateRoom(rank?.score || 0)}</b>, rank{" "}
                <b>{commonService.formateRoom(rank?.rank || 1)}</b> of {commonService.formateRoom(dates.length)}
                {!rank?.onStrip && " (not on the strip)"}
              </div>
            </div>
            <div className="stats">
              <div className="stat">
                <div className="k">On the books</div>
                <div className="v num">{model.P1(signals.otb)}</div>
                <div className="d">
                  <span className={occupancyDelta >= 0 ? "up" : "down"}>
                    {occupancyDelta >= 0 ? "+" : ""}
                    {model.r2(occupancyDelta)} pts
                  </span>{" "}
                  vs. {model.P1(signals.stly)} STLY
                </div>
              </div>
              <div className="stat">
                <div className="k">Forecast</div>
                <div className="v num">{model.P1(signals.fcst)}</div>
                <div className="d">
                  {commonService.formateRoom(Math.round((signals.fcst / 100) * capacity))} of{" "}
                  {commonService.formateRoom(capacity)} rooms
                </div>
              </div>
              <div className="stat">
                <div className="k">ADR</div>
                <div className="v num">{commonService.formateAmount(signals.adr)}</div>
                <div className="d">
                  <span className={adrDelta >= 0 ? "up" : "down"}>
                    {adrDelta >= 0 ? "+" : ""}
                    {commonService.formateAmount(adrDelta)}
                  </span>{" "}
                  vs. {commonService.formateAmount(signals.stlyAdr)} STLY
                </div>
              </div>
              <div className="stat">
                <div className="k">Hurdle net rate</div>
                <div className="v num">{commonService.formateAmount(analysis.h)}</div>
                <div className="d">Lowest net worth accepting</div>
              </div>
            </div>
            <div className="quad">
              <h3>Where every date sits</h3>
              <p className="hint">
                All {commonService.formateRoom(dates.length)} dates. Bigger dots are on the strip. Select a dot to
                open its reservations.
              </p>
              <Quadrant
                model={model}
                selectedDate={selectedDate}
                selectDate={selectDate}
                openReservations={openReservations}
                propertyCode={propertyCode}
              />
            </div>
            <div className="lever">
              {analysis.w && LEVERS[analysis.w]?.text
                ? parse(LEVERS[analysis.w].text)
                : null}
            </div>
            <TestSignals
              signals={signals}
              selectedIndex={selectedIndex}
              overrides={overrides}
              onSignalChange={changeSignal}
              onReset={reset}
              model={model}
              propertyCode={propertyCode}
              api={api}
            />
          </section>
          <section
            className="panel right-panel"
            aria-label="Stay date drill-down"
            style={
              leftHeight && typeof window !== "undefined" && window.innerWidth > 980
                ? {
                  minHeight: `${leftHeight}px`,
                  maxHeight: `${leftHeight}px`,
                  height: `${leftHeight}px`,
                }
                : undefined
            }
          >
            <div className="act-head">
              <div>
                <h2>Recommended actions</h2>
                <p>
                  {model.md(selectedDate)}, {commonService.formateRoom(analysis.otbRooms)} rooms on the
                  books across {commonService.formateRoom(analysis.rows.length)} rate plans
                </p>
              </div>
              <div className="act-btns">
                <button
                  // className="ghost"
                  className="approve"
                  type="button"
                  id={commonService.generateGlobalClickTrackerID(
                    "YieldRadar",
                    "date-ranking-btn",
                    propertyCode,
                  )}
                  disabled={api === 0}
                  onClick={() => {
                    if (api === 0) return;
                    setShowRanking(true);
                  }}
                >
                  Date ranking
                </button>
                <button
                  // className="ghost"
                  className="approve"
                  type="button"
                  id={commonService.generateGlobalClickTrackerID(
                    "YieldRadar",
                    "view-reservations-btn",
                    propertyCode,
                  )}
                  disabled={api === 0}
                  onClick={() => {
                    if (api === 0) return;
                    openReservations();
                  }}
                >
                  View reservations
                </button>
                <button
                  className="approve"
                  type="button"
                  id={commonService.generateGlobalClickTrackerID(
                    "YieldRadar",
                    "approve-actions-btn",
                    propertyCode,
                  )}
                  disabled={api === 0 || !!approved[selectedDate] || !actionable.length}
                  onClick={() => {
                    if (api === 0) return;
                    approve();
                  }}
                >
                  {approved[selectedDate]
                    ? "Changes approved"
                    : actionable.length
                      ? `Approve ${commonService.formateRoom(actionable.length)} change${actionable.length === 1 ? "" : "s"}`
                      : "Nothing to approve"}
                </button>
              </div>
            </div>
            <div
              className="tabs"
              role="tablist"
              aria-label="Stay date details"
              ref={tabsRef}
            >
              {Object.entries(TABS).map(([key, label]) => {
                const tabId = commonService.generateGlobalClickTrackerID(
                  "YieldRadar",
                  `tab-${key}`,
                  propertyCode,
                );
                return (
                  <button
                    key={key}
                    className="tab"
                    type="button"
                    role="tab"
                    id={tabId}
                    aria-controls="stay-detail"
                    aria-selected={key === tab}
                    tabIndex={key === tab ? 0 : -1}
                    disabled={api === 0}
                    onClick={() => {
                      if (api === 0) return;
                      setTab(key);
                    }}
                    onKeyDown={(event) => {
                      if (api === 0) return;
                      const keys = Object.keys(TABS),
                        index = keys.indexOf(key);
                      const next =
                        event.key === "ArrowRight"
                          ? keys[(index + 1) % keys.length]
                          : event.key === "ArrowLeft"
                            ? keys[(index + keys.length - 1) % keys.length]
                            : event.key === "Home"
                              ? keys[0]
                              : event.key === "End"
                                ? keys[keys.length - 1]
                                : null;
                      if (next) {
                        event.preventDefault();
                        setTab(next);
                        const nextTabId = commonService.generateGlobalClickTrackerID(
                          "YieldRadar",
                          `tab-${next}`,
                          propertyCode,
                        );
                        tabsRef.current?.querySelector(`#${nextTabId}`)?.focus();
                      }
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div
              className={`tabpane on ${tab === "actions" ? "is-actions-tab" : ""} tab-${tab}`}
              id="stay-detail"
              role="tabpanel"
              aria-labelledby={commonService.generateGlobalClickTrackerID(
                "YieldRadar",
                `tab-${tab}`,
                propertyCode,
              )}
            >
              {tab === "actions" ? (
                <Actions
                  analysis={analysis}
                  model={model}
                  excluded={uncheckedKeys}
                  approvedKeys={approved[selectedDate]}
                  onToggle={toggle}
                  onPlan={(rowKey) => {
                    if (api === 0) return;
                    setDrawerKey(rowKey);
                  }}
                  propertyCode={propertyCode}
                  api={api}
                />
              ) : (
                <DetailContent
                  html={content}
                  onPlan={(rowKey) => {
                    if (api === 0) return;
                    setDrawerKey(rowKey);
                  }}
                  onCopyDate={() => {
                    if (api === 0) return;
                    copy(model.datePayload(analysis));
                  }}
                  onCopyPromo={() => {
                    if (api === 0) return;
                    copy(model.promoPayload(model.promoForDate(analysis)));
                  }}
                  propertyCode={propertyCode}
                  disabled={api === 0}
                />
              )}
            </div>
          </section>
        </div>
        {showRanking && api !== 0 && (
          <Modal
            show={showRanking}
            onHide={() => setShowRanking(false)}
            size="xl"
            centered
            enforceFocus={false}
            className="yield-radar-reservations-modal-container"
            dialogClassName="yield-radar-reservations-modal-dialog"
            contentClassName="yield-radar-reservations-modal-content"
          >
            <Modal.Header
              closeButton
              id={commonService.generateGlobalClickTrackerID(
                "YieldRadar",
                "ranking-modal-close",
                propertyCode,
              )}
            >
              <div>
                <Modal.Title id="contained-modal-title-vcenter">
                  <span>
                    How the strip is chosen
                    <small className="reservations-modal-subtitle">
                      {" "}({dates.length <= Math.min(CONFIG.screener?.topN || 14, dates.length || 14)
                        ? `All ${commonService.formateRoom(dates.length)} dates in the horizon are scored and shown on the strip. Select a row to open that date.`
                        : `Every date in the horizon is scored. The top ${commonService.formateRoom(Math.min(CONFIG.screener?.topN || 14, dates.length || 14))} by attention score go on the strip. Select a row to open that date.`})
                    </small>
                  </span>
                </Modal.Title>
              </div>
            </Modal.Header>
            <Modal.Body className="p-0">
              <div className="YieldRadar reservations-modal-root">
                <Ranking
                  model={model}
                  selectDate={selectDate}
                  totalDates={dates.length}
                  onOpenEvent={openEventModal}
                  close={() => setShowRanking(false)}
                  propertyCode={propertyCode}
                  api={api}
                />
              </div>
            </Modal.Body>
          </Modal>
        )}
        {showReservations && api !== 0 && (
          <Modal
            show={showReservations}
            onHide={() => setShowReservations(false)}
            size="xl"
            centered
            enforceFocus={false}
            className="yield-radar-reservations-modal-container"
            dialogClassName="yield-radar-reservations-modal-dialog"
            contentClassName="yield-radar-reservations-modal-content"
          >
            <Modal.Header
              closeButton
              id={commonService.generateGlobalClickTrackerID(
                "YieldRadar",
                "reservations-modal-close",
                propertyCode,
              )}
            >
              <div>
                <Modal.Title id="contained-modal-title-vcenter">
                  <span>
                    Reservations for {model.longDate(analysis.s.d)}
                    <small className="reservations-modal-subtitle">
                      {" "}({analysis?.s?.dba != null && !isNaN(analysis.s.dba) ? `${commonService.formateRoom(analysis.s.dba)} days out, ` : ""}{Q[analysis.q]?.label || ""}. Every reservation that covers this night as of {formatCalendarDate(asOfDate || AS_OF, "MMMM D, YYYY")})
                    </small>
                  </span>
                </Modal.Title>
              </div>
            </Modal.Header>
            <Modal.Body className="p-0">
              <div className="YieldRadar reservations-modal-root">
                <Reservations
                  analysis={analysis}
                  model={model}
                  filters={filters}
                  setFilters={setFilters}
                  close={() => setShowReservations(false)}
                  notify={notify}
                  panelRef={reservationsRef}
                  reservationsLoading={reservationsLoading}
                  propertyCode={propertyCode}
                />
              </div>
            </Modal.Body>
          </Modal>
        )}
        <Campaigns
          model={model}
          planned={planned}
          setPlanned={setPlanned}
          copy={copy}
          notify={notify}
          propertyCode={propertyCode}
          onOpenEvent={openEventModal}
          api={api}
        />
      </div>
      {drawerRow && root && (
        <Drawer
          key={drawerKey}
          row={drawerRow}
          analysis={analysis}
          model={model}
          close={() => setDrawerKey(null)}
          openReservations={openReservations}
          copy={copy}
          propertyCode={propertyCode}
          api={api}
        />
      )}
      {/* Hidden combined table for Send Mail and automated table exporters */}
      <div
        style={{ display: "none" }}
        className="snapshotrow"
        rowtitle="Yield Radar"
      >
        <table id={combinedTableId} border="1" style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th
                colSpan={14}
                style={{
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "8px",
                }}
              >
                How the strip is chosen (Date Ranking)
              </th>
            </tr>
            <tr>
              <th
                colSpan={14}
                style={{
                  textAlign: "left",
                  fontSize: "11px",
                  fontWeight: "normal",
                  padding: "4px",
                }}
              >
                Every date in the horizon is scored. The top 14 by attention score go on the strip. Select a row to open that date.
              </th>
            </tr>
            <tr style={{ fontWeight: "bold" }}>
              <th style={{ textAlign: "center", padding: "6px" }}>RANK</th>
              <th style={{ textAlign: "left", padding: "6px" }}>STAY DATE</th>
              <th style={{ textAlign: "right", padding: "6px" }}>DAYS OUT</th>
              <th style={{ textAlign: "left", padding: "6px" }}>DIAGNOSIS</th>
              <th style={{ textAlign: "right", padding: "6px" }}>SCORE</th>
              <th style={{ textAlign: "right", padding: "6px" }}>ACTIONS</th>
              <th style={{ textAlign: "right", padding: "6px" }}>REVENUE AT STAKE</th>
              <th colSpan={7} style={{ textAlign: "left", padding: "6px" }}>WHY IT IS FLAGGED</th>
            </tr>
          </thead>
          <tbody>
            {model.SCR.order.slice(0, 20).map(model.rankRecord).map((r) => (
              <tr key={r.stayDate}>
                <td style={{ textAlign: "center", padding: "6px" }}>{r.rank}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>
                  {model.dow(r.stayDate)} {model.md(r.stayDate)}{r.event ? ` (${r.event})` : ""}
                </td>
                <td style={{ textAlign: "right", padding: "6px" }}>{commonService.formateRoom(r.daysOut ?? r.dba)}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>{Q[r.diagnosis]?.label || r.diagnosis}</td>
                <td style={{ textAlign: "right", padding: "6px" }}>{commonService.formateRoom(r.score)}</td>
                <td style={{ textAlign: "right", padding: "6px" }}>{commonService.formateRoom(r.actions)}</td>
                <td style={{ textAlign: "right", padding: "6px" }}>{commonService.formateAmount(r.revenueAtStake)}</td>
                <td colSpan={7} style={{ textAlign: "left", padding: "6px" }}>{r.whyFlagged || ""}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={14} style={{ height: "20px", border: "none" }}>&nbsp;</td>
            </tr>
            <tr>
              <th
                colSpan={14}
                style={{
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "8px",
                }}
              >
                Reservations for {model.longDate(analysis.s.d)}
              </th>
            </tr>
            <tr>
              <th
                colSpan={14}
                style={{
                  textAlign: "left",
                  fontSize: "11px",
                  fontWeight: "normal",
                  padding: "4px",
                }}
              >
                {commonService.formateRoom(analysis.s.dba)} days out, {Q[analysis.q]?.label || ""}. Every reservation that covers this night as of {formatCalendarDate(asOfDate || AS_OF, "MMMM D, YYYY")}.
              </th>
            </tr>
            <tr style={{ fontWeight: "bold" }}>
              <th style={{ textAlign: "left", padding: "6px" }}>CONFIRMATION</th>
              <th style={{ textAlign: "left", padding: "6px" }}>GUEST</th>
              <th style={{ textAlign: "left", padding: "6px" }}>BOOKED</th>
              <th style={{ textAlign: "right", padding: "6px" }}>LEAD (DAYS)</th>
              <th style={{ textAlign: "left", padding: "6px" }}>ARRIVAL</th>
              <th style={{ textAlign: "right", padding: "6px" }}>NIGHTS</th>
              <th style={{ textAlign: "left", padding: "6px" }}>ROOM TYPE</th>
              <th style={{ textAlign: "left", padding: "6px" }}>RATE PLAN</th>
              <th style={{ textAlign: "left", padding: "6px" }}>CHANNEL</th>
              <th style={{ textAlign: "left", padding: "6px" }}>SEGMENT</th>
              <th style={{ textAlign: "right", padding: "6px" }}>RATE</th>
              <th style={{ textAlign: "right", padding: "6px" }}>NET</th>
              <th style={{ textAlign: "right", padding: "6px" }}>VS. TODAY</th>
              <th style={{ textAlign: "left", padding: "6px" }}>STATUS</th>
            </tr>
            {model.reservations(analysis).map((row, idx) => (
              <tr key={`${row.conf}-${idx}`}>
                <td style={{ textAlign: "left", padding: "6px" }}>{row.conf}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>{row.guest}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>{model.md(row.booked)}</td>
                <td style={{ textAlign: "right", padding: "6px" }}>{commonService.formateRoom(row.lead)}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>{model.md(row.arrival)}</td>
                <td style={{ textAlign: "right", padding: "6px" }}>{commonService.formateRoom(row.nights)}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>{row.room}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>{row.plan}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>{row.channel}</td>
                <td style={{ textAlign: "left", padding: "6px" }}>{row.segment}</td>
                <td style={{ textAlign: "right", padding: "6px" }}>{commonService.formateAmount(row.rate)}</td>
                <td style={{ textAlign: "right", padding: "6px" }}>{commonService.formateAmount(row.net)}</td>
                <td style={{ textAlign: "right", padding: "6px" }}>
                  {row.fixed ? "-" : (row.vsToday > 0 ? "+" : "") + commonService.formateAmount(row.vsToday)}
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showTicketModal && api !== 0 && (
        <EventViewModal
          showModal={showTicketModal}
          closeModal={() => setShowTicketModal(false)}
          eventDetails={eventListPropsRef.current}
          selectedDate={selectedEventDateRef.current}
          getEventList={async () => {
            if (api === 0) return;
            if (getEventList) {
              getEventList();
            }
            await loadEventsIfEmpty(true);
          }}
        />
      )}
    </div>
  );
}
